import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import './business/business.css';
import { Send, RotateCcw, Plus, Brain, Bot, Database, Key, Menu, X, Square, Paperclip, Globe, Package, FileText, Image as ImageIcon, Download, Sheet, Mic, MicOff, Camera, Video, Sparkles, Volume2, VolumeX, Smartphone, Newspaper, Music, Headphones, Briefcase } from 'lucide-react';
import { db, getSetting, setSetting, uid, hardResetDB } from './lib/db';
import { streamChat } from './lib/api';
import { MODELS, DEFAULT_MODEL, THEMES, DEFAULT_SYSTEM, SAMPLE_PROMPTS } from './lib/models';
import { MarkdownView } from './components/MarkdownView';
import { fileToBase64, validateFile, ACCEPT_ATTR, humanSize } from './lib/files';
import { extractCodeBlocks, downloadAsZip, downloadBlock, downloadBlockAsXlsx, labelForExt, cloneUrl } from './lib/clone';
import { VoanhLogo } from './components/VoanhLogo';
import { ToastProvider, useToast } from './components/Toast';
import { ApiModal, AgentModal, DataModal } from './components/Modals';
import { MemoryPanel } from './components/MemoryPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { WebcamPanel } from './components/WebcamPanel';
import { ImageGenModal, VideoGenModal } from './components/MediaModals';
import { PwaGenModal } from './components/PwaGenModal';
import { PromoGenModal } from './components/PromoGenModal';
import { generateAudioStudio } from './lib/media';
import { downloadPwaZip } from './lib/pwa';
import { startSTT, speak, stopSpeaking, isSTTSupported, isTTSSupported } from './lib/voice';
import { detectIntent } from './lib/intent';
import { generateImage, generateVideo, pollVideoUntilDone, webSearch } from './lib/media';
import { fetchLiveModels } from './lib/discovery';
import { AiNewsPanel } from './components/AiNewsPanel';
import { BusinessOnboarding } from './business/BusinessOnboarding';
import { BusinessHub } from './business/BusinessHub';
import { MasterAdmin } from './business/MasterAdmin';
import { LandingPage } from './business/LandingPage';
import { PWAInstallPrompt } from './business/PWAInstallPrompt';
import './business/admin.css';
import { clearLocalWorkspace } from './lib/biz';
import { useAuth } from './hooks/useAuth';
import { useWorkspace } from './hooks/useWorkspace';
import { initDiagnostics } from './lib/diagnostics';
import { initPWA, registerInstallPrompt } from './lib/pwa-install';

// Wire global error reporting + PWA install prompt as soon as the module
// is imported. Each side-effect is wrapped so a failure in one cannot
// prevent the React tree from mounting (critical for Safari private mode
// where some APIs are restricted).
try { initDiagnostics(); }       catch (e) { console.warn('YNX bootstrap: initDiagnostics failed', e); }
try { registerInstallPrompt(); } catch (e) { console.warn('YNX bootstrap: registerInstallPrompt failed', e); }
try { initPWA(); }               catch (e) { console.warn('YNX bootstrap: initPWA failed', e); }

const buildSystemPrompt = (agent, memories) => {
  let sys = DEFAULT_SYSTEM;
  if (agent) {
    sys = `${agent.instructions}\n\n[Persona actif : ${agent.name}]`;
  }
  if (memories && memories.length > 0) {
    sys += `\n\n[Mémoire globale — faits à retenir]\n` + memories.map((m) => `- ${m.text}`).join('\n');
  }
  return sys;
};

const VoanhApp = () => {
  const toast = useToast();

  // State
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [theme, setTheme] = useState('corporate');
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState(null);
  const [memories, setMemories] = useState([]);

  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);

  const [showApiModal, setShowApiModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{name, mime_type, data_base64, size}]
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneUrlInput, setCloneUrlInput] = useState('');
  const [cloning, setCloning] = useState(false);

  // Multimodal state
  const [showWebcam, setShowWebcam] = useState(false);
  const [showImageGen, setShowImageGen] = useState(false);
  const [imageGenRef, setImageGenRef] = useState(null); // base64 reference for img2img
  const [showVideoGen, setShowVideoGen] = useState(false);
  const [videoSource, setVideoSource] = useState(null); // base64 source for video
  const [showPwaGen, setShowPwaGen] = useState(false);
  const [showPromoGen, setShowPromoGen] = useState(false);
  const [liveModels, setLiveModels] = useState([]);
  const [showNews, setShowNews] = useState(false);
  const [recording, setRecording] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);

  // ─── BUSINESS SaaS state (via useWorkspace hook) ────────
  const {
    workspace, setWorkspace,
    workspaceLoading,
    workspaceBlocked,
    showOnboarding, setShowOnboarding,
    onboardingMode, setOnboardingMode,
  } = useWorkspace();
  const [view, setView] = useState('business'); // 'business' | 'chat'

  // ─── AUTH state (via useAuth hook) ──────────────────────
  // Encapsulates Emergent Google OAuth: detects #session_id= in URL during
  // render (anti race-condition), exchanges it, validates existing tokens.
  const { currentUser, setCurrentUser, authCallbackBusy } = useAuth();
  // ─── MASTER ADMIN detection ─────────────────────────────
  // URL like /?admin=YOUR_SECRET enters Carl's admin panel.
  const adminSecretFromUrl = (() => {
    try { return new URLSearchParams(window.location.search).get('admin') || ''; }
    catch (_e) { return ''; }
  })();
  const isAdminRoute = adminSecretFromUrl !== '' || (typeof window !== 'undefined' && window.location.pathname === '/admin');

  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const fileInputRef = useRef(null);
  const sttRef = useRef(null);
  const lastSpokenIdRef = useRef(null);

  // Initial load — runs once on mount only
  useEffect(() => {
    (async () => {
      try {
        const [savedModel, savedTheme, savedAgentId, savedChatId, mems, agentList] = await Promise.all([
          getSetting('model', DEFAULT_MODEL),
          getSetting('theme', 'corporate'),
          getSetting('current_agent', null),
          getSetting('current_chat', null),
          db.getAll('global_memory'),
          db.getAll('agents'),
        ]);
        setModel(savedModel);
        setTheme(savedTheme);
        document.documentElement.dataset.theme = savedTheme;
        setMemories(mems);
        setAgents(agentList);
        if (savedAgentId) {
          const a = agentList.find((x) => x.id === savedAgentId);
          if (a) setAgent(a);
        }
        if (savedChatId) {
          const c = await db.get('chats', savedChatId);
          if (c?.messages) {
            setChatId(savedChatId);
            setMessages(c.messages);
            return;
          }
        }
        // start fresh chat
        const newId = uid();
        setChatId(newId);
      } catch (err) {
        // Storage corrupted or quota exceeded → recover instead of blocking.
        console.warn('YNX: local storage hydration failed, auto-recovering', err);
        try { await hardResetDB(); } catch (resetErr) { console.warn('YNX: hardResetDB also failed', resetErr); }
        setChatId(uid());
        if (typeof window !== 'undefined') {
          // Tiny banner so the user knows something happened
          window.setTimeout(() => {
            try {
              window.alert(
                "YNX a détecté un stockage local corrompu et l'a réinitialisé automatiquement. "
                + 'Tu peux recommencer à utiliser le site normalement.'
              );
            } catch (alertErr) { console.debug('YNX: alert() blocked', alertErr); }
          }, 600);
        }
      }
    })();
    // Live models discovery (non-blocking)
    fetchLiveModels().then((extra) => {
      if (extra && extra.length) setLiveModels(extra);
    }).catch(() => {});

    // NOTE: workspace hydration is owned by `useWorkspace`, OAuth callback +
    // /me validation is owned by `useAuth`. Both run their own effects on
    // mount — keep this useEffect for chat/agents/memories hydration only.
  }, []);

  // Save messages to DB when they change
  useEffect(() => {
    if (!chatId) return;
    const t = setTimeout(() => {
      db.put('chats', { id: chatId, messages, updatedAt: Date.now() });
      setSetting('current_chat', chatId);
    }, 200);
    return () => clearTimeout(t);
  }, [messages, chatId]);

  // Auto-scroll
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, streaming]);

  // Auto-speak the last assistant message when streaming ends (if TTS enabled)
  useEffect(() => {
    if (!ttsEnabled || streaming || !isTTSSupported()) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || !last.content) return;
    if (lastSpokenIdRef.current === last.id) return;
    lastSpokenIdRef.current = last.id;
    // Strip markdown / code blocks for clean speech
    const plain = last.content
      .replace(/```[\s\S]*?```/g, ' (bloc de code) ')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/[#>•·▸]/g, '');
    if (plain.trim().length > 0) {
      speak({ text: plain.slice(0, 3000), lang: 'fr-FR' });
    }
  }, [messages, streaming, ttsEnabled]);

  // Persist model/theme
  useEffect(() => { setSetting('model', model); }, [model]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    setSetting('theme', theme);
  }, [theme]);
  useEffect(() => { setSetting('current_agent', agent?.id || null); }, [agent]);

  const refreshAgents = useCallback(async () => {
    const list = await db.getAll('agents');
    setAgents(list);
  }, []);
  const refreshMemories = useCallback(async () => {
    const list = await db.getAll('global_memory');
    setMemories(list);
  }, []);

  // Auto-grow textarea
  const handleInput = (e) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const resetTextarea = () => {
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  const newChat = () => {
    // If streaming, abort first then create a new chat
    if (streaming && abortRef.current) {
      try { abortRef.current.abort(); } catch (err) { console.warn('YNX: abort failed during newChat', err); }
      abortRef.current = null;
      setStreaming(false);
    }
    const id = uid();
    setChatId(id);
    setMessages([]);
    setInput('');
    resetTextarea();
    toast('Nouvelle conversation', 'info');
  };

  const clearChat = () => {
    if (streaming && abortRef.current) {
      try { abortRef.current.abort(); } catch (err) { console.warn('YNX: abort failed during clearChat', err); }
      abortRef.current = null;
      setStreaming(false);
    }
    setMessages([]);
    toast('Conversation effacée', 'info');
  };

  const stopStream = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setStreaming(false);
    }
  };

  const send = async () => {
    const txt = input.trim();
    if ((!txt && attachments.length === 0) || streaming) return;
    const finalText = txt || (attachments.length > 0 ? '(Analyse les pièces jointes ci-dessous)' : '');

    // ─── INTENT DETECTION ───
    const imgAttachments = attachments.filter((a) => (a.mime_type || '').startsWith('image/'));
    const intent = detectIntent(finalText, imgAttachments.length > 0);

    // Route to image generation tool
    if (intent === 'image') {
      await handleAutoImage(finalText, imgAttachments[0]);
      return;
    }
    // Route to video generation tool
    if (intent === 'video') {
      await handleAutoVideo(finalText, imgAttachments[0]);
      return;
    }
    // Web search → inject results then chat
    let searchInjection = '';
    if (intent === 'search') {
      try {
        toast('🌐 Recherche web en cours…', 'info');
        const search = await webSearch(finalText, 6);
        const lines = (search.results || []).map((r, i) =>
          `${i + 1}. ${r.title}\n   ${r.url}\n   ${r.snippet}`
        ).join('\n\n');
        if (lines) {
          searchInjection = `\n\n[Résultats web récents pour "${search.query}" — utilise-les pour répondre, cite les URLs]\n${lines}`;
        }
      } catch (err) {
        console.warn('YNX: web search failed', err);
        toast(`Recherche web : ${err.message}`, 'error');
      }
    }

    // ─── Default: stream chat via LLM ───
    const attachmentMeta = attachments.map((a) => ({ name: a.name, mime_type: a.mime_type, size: a.size }));
    const userMsg = { id: uid(), role: 'user', content: finalText, attachments: attachmentMeta, ts: Date.now() };
    const asstId = uid();
    const asstMsg = { id: asstId, role: 'assistant', content: '', ts: Date.now() };
    const history = [...messages, userMsg];
    setMessages([...history, asstMsg]);
    setInput('');
    resetTextarea();
    setStreaming(true);

    // Snapshot attachments then clear
    const attsToSend = attachments;
    setAttachments([]);

    const sysPrompt = buildSystemPrompt(agent, memories);

    let acc = '';
    // Resolve which key to send based on api_mode in settings
    let customApiKey = null;
    try {
      const apiMode = await db.get('settings', 'api_mode');
      const m = apiMode?.value || 'emergent';
      if (m === 'universal') {
        const k = await db.get('settings', 'api_key_universal');
        if (k?.value) customApiKey = k.value;
      } else if (m === 'custom') {
        const modelDef = MODELS.find((x) => x.id === model);
        const prov = modelDef?.provider;
        const keyId = prov === 'OpenAI' ? 'api_key_openai' : prov === 'Anthropic' ? 'api_key_anthropic' : prov === 'Google' ? 'api_key_gemini' : null;
        if (keyId) {
          const k = await db.get('settings', keyId);
          if (k?.value) customApiKey = k.value;
        }
      }
      // 'emergent' mode: customApiKey stays null -> backend uses EMERGENT_LLM_KEY
    } catch (err) { console.warn('YNX: failed to read API key from settings', err); }

    const handle = await streamChat({
      sessionId: chatId,
      model,
      systemMessage: sysPrompt,
      messages: history.slice(0, -1).map((m) => ({ role: m.role, content: m.content })),
      userMessage: finalText + searchInjection,
      customApiKey,
      attachments: attsToSend,
      onRoute: (info) => {
        const picked = MODELS.find((mm) => mm.id === info.picked);
        const label = picked ? `${picked.name} · ${picked.provider}` : `${info.model}`;
        setMessages((cur) => cur.map((m) => m.id === asstId ? { ...m, routedTo: label } : m));
      },
      onDelta: (chunk) => {
        acc += chunk;
        // ─── EARLY REFUSAL ABORT (saves credits) ───
        // As soon as we detect a refusal pattern in the first ~200 chars, abort
        // the stream and reroute to image gen — no more wasted tokens on excuses.
        const EARLY_REFUSAL_RX = /(je ne peux pas (?:cr[ée]er|g[ée]n[ée]rer|fournir|produire|directement)|malheureusement,?\s*je ne peux pas|je suis d[ée]sol[ée] pour la confusion|en tant qu['']?ia (?:textuelle|de texte)|cannot (?:create|generate|provide))/i;
        if (acc.length < 400 && EARLY_REFUSAL_RX.test(acc)) {
          const looksLikeVisualRequest = /(photo|image|portrait|visuel|illustration|personnage|character|logo|design|concept|sc[èe]ne|avatar|affiche|cover|pochette|dessin|art|rendu)/i.test(finalText);
          if (looksLikeVisualRequest) {
            try { abortRef.current?.abort?.(); } catch (abortErr) { console.debug('YNX: stream abort already done', abortErr); }
            abortRef.current = null;
            setStreaming(false);
            setMessages((cur) => cur.filter((m) => m.id !== asstId));
            toast('🛡️ Refus du LLM intercepté — bascule directe sur Nano Banana (gratuit)', 'info');
            setTimeout(() => handleAutoImage(finalText, imgAttachments[0] || null), 30);
            return;
          }
        }
        setMessages((cur) => cur.map((m) => m.id === asstId ? { ...m, content: acc } : m));
      },
      onDone: () => {
        setStreaming(false);
        abortRef.current = null;
        // ─── REFUSAL SAFETY NET ───
        // If the model refused to do something YNX can actually do, auto-route to image gen.
        const REFUSAL_RX = /(je ne peux pas (?:cr[ée]er|g[ée]n[ée]rer|fournir)|malheureusement,? je ne peux pas|je suis d[ée]sol[ée] pour la confusion|cannot (?:create|generate|provide) (?:visual|image|files|download)|en tant qu['']?ia (?:textuelle|de texte)|je ne peux pas directement)/i;
        if (acc && REFUSAL_RX.test(acc)) {
          const looksLikeVisualRequest = /(photo|image|portrait|visuel|illustration|personnage|character|logo|design|concept|sc[èe]ne|avatar|affiche|cover|pochette)/i.test(finalText);
          if (looksLikeVisualRequest) {
            toast('YNX a tenté de refuser — redirection vers la génération d\'image automatique', 'info');
            // Replace the refusal message and route
            setMessages((cur) => cur.filter((m) => m.id !== asstId));
            // Small delay to avoid React batching glitch
            setTimeout(() => handleAutoImage(finalText, imgAttachments[0] || null), 50);
          }
        }
      },
      onError: (err) => {
        setStreaming(false);
        abortRef.current = null;
        setMessages((cur) => cur.map((m) => m.id === asstId ? { ...m, content: acc || `⚠ Erreur : ${err.message}`, error: true } : m));
        toast(`Erreur : ${err.message}`, 'error');
      },
    });
    abortRef.current = handle;
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const selectPrompt = (p) => {
    setInput(p);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ─── Speech-to-Text (microphone) ───
  const toggleMic = () => {
    if (recording) {
      sttRef.current?.stop();
      sttRef.current = null;
      setRecording(false);
      return;
    }
    if (!isSTTSupported()) {
      toast('Reconnaissance vocale non supportée (utilisez Chrome / Edge)', 'error');
      return;
    }
    setRecording(true);
    sttRef.current = startSTT({
      lang: 'fr-FR',
      onResult: ({ transcript, isFinal }) => {
        if (isFinal) {
          setInput((cur) => (cur ? cur + ' ' : '') + transcript.trim());
        }
      },
      onError: (err) => {
        console.warn('YNX: STT error', err);
        toast(`Micro : ${err.message}`, 'error');
        setRecording(false);
        sttRef.current = null;
      },
      onEnd: () => {
        setRecording(false);
        sttRef.current = null;
      },
    });
  };

  // ─── Text-to-Speech (toggle) ───
  const toggleTts = () => {
    setTtsEnabled((v) => {
      const next = !v;
      if (!next) stopSpeaking();
      toast(next ? '🔊 Lecture vocale activée' : '🔇 Lecture vocale désactivée', 'info');
      return next;
    });
  };

  // ─── Attachments ───
  const handleFilesPicked = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const newOnes = [];
    for (const f of list) {
      const check = validateFile(f);
      if (!check.ok) { toast(`${f.name} : ${check.error}`, 'error'); continue; }
      try {
        const b64 = await fileToBase64(f);
        newOnes.push({
          id: uid(),
          name: f.name,
          mime_type: f.type,
          size: f.size,
          data_base64: b64,
        });
      } catch (err) {
        console.warn('YNX: file encoding failed', err);
        toast(`Échec de lecture : ${f.name}`, 'error');
      }
    }
    if (newOnes.length > 0) {
      setAttachments((cur) => [...cur, ...newOnes]);
      toast(`${newOnes.length} pièce(s) jointe(s) ajoutée(s)`, 'success');
    }
  };

  const removeAttachment = (id) => {
    setAttachments((cur) => cur.filter((a) => a.id !== id));
  };

  // Add a base64 image (e.g. webcam snapshot or generated image) as attachment
  const attachBase64Image = (b64, name = null) => {
    const safeName = name || `snapshot-${Date.now()}.png`;
    setAttachments((cur) => [...cur, {
      id: uid(),
      name: safeName,
      mime_type: 'image/png',
      size: Math.round(b64.length * 3 / 4),
      data_base64: b64,
    }]);
  };

  // ─── Clone URL ───
  const handleCloneUrl = async () => {
    const url = cloneUrlInput.trim();
    if (!url) { toast('Saisissez une URL', 'error'); return; }
    setCloning(true);
    try {
      const data = await cloneUrl(url);
      const prompt = `Je veux cloner ce site web et te demander d'écrire un clone fidèle.

URL source : ${data.url}
Titre : ${data.title || '(sans titre)'}
Description : ${data.description || '(aucune)'}

Génère-moi un clone fonctionnel **en un fichier index.html complet** (HTML + CSS inline + JS inline) qui reproduit l'apparence et la structure du site source. Conserve les couleurs, la typographie, la mise en page. Utilise les images suivantes quand pertinent :

${(data.images || []).slice(0, 10).map((i) => `- ${i.src}${i.alt ? ` (${i.alt})` : ''}`).join('\n') || '(aucune image détectée)'}

Voici le HTML nettoyé du site source (pour analyse de la structure) :

\`\`\`html
${(data.html || '').slice(0, 30000)}
\`\`\`

Renvoie UNIQUEMENT le code dans un bloc \`\`\`html, sans explication.`;
      setShowCloneModal(false);
      setCloneUrlInput('');
      setInput(prompt);
      toast(`Site récupéré (${data.images?.length || 0} images, ${(data.html?.length || 0) / 1000 | 0} Ko HTML)`, 'success');
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err) {
      console.warn('YNX: clone failed', err);
      toast(err.message, 'error');
    } finally {
      setCloning(false);
    }
  };

  // ─── ZIP download ───
  const handleDownloadZip = async (message) => {
    try {
      const blocks = extractCodeBlocks(message.content);
      if (blocks.length === 0) {
        toast('Aucun bloc de code détecté dans cette réponse', 'error');
        return;
      }
      const fname = await downloadAsZip(blocks, 'ynx-project');
      toast(`Téléchargé : ${fname}`, 'success');
    } catch (err) {
      console.warn('YNX: ZIP export failed', err);
      toast(`Erreur ZIP : ${err.message}`, 'error');
    }
  };

  // ─── Single file download ───
  const handleDownloadOne = (block, idx) => {
    try {
      const name = downloadBlock(block, idx);
      toast(`Téléchargé : ${name}`, 'success');
    } catch (err) {
      console.warn('YNX: download single failed', err);
      toast(`Erreur : ${err.message}`, 'error');
    }
  };

  const handleDownloadXlsx = (block, idx) => {
    try {
      const name = downloadBlockAsXlsx(block, idx);
      toast(`Excel exporté : ${name}`, 'success');
    } catch (err) {
      console.warn('YNX: XLSX export failed', err);
      toast(`Erreur XLSX : ${err.message}`, 'error');
    }
  };

  // ─── AUTO IMAGE GENERATION (intent routed) ───
  const handleAutoImage = async (prompt, referenceAttachment) => {
    const userMsg = {
      id: uid(),
      role: 'user',
      content: prompt,
      attachments: referenceAttachment ? [{ name: referenceAttachment.name, mime_type: referenceAttachment.mime_type }] : [],
      ts: Date.now(),
    };
    const asstId = uid();
    const placeholder = {
      id: asstId,
      role: 'assistant',
      content: referenceAttachment
        ? '🎨 Génération de votre photo en cours (Nano Banana)…'
        : '🎨 Création de l\'image en cours (Nano Banana)…',
      ts: Date.now(),
      pending: true,
    };
    setMessages((cur) => [...cur, userMsg, placeholder]);
    setInput('');
    resetTextarea();
    setAttachments([]);
    setStreaming(true);
    try {
      const result = await generateImage({
        prompt,
        referenceImageBase64: referenceAttachment?.data_base64 || null,
      });
      const img = (result.images || [])[0];
      if (!img?.data_base64) {
        throw new Error(result.text || 'Aucune image retournée');
      }
      setMessages((cur) => cur.map((m) => m.id === asstId
        ? { ...m, content: result.text || 'Voici votre image :', generatedImage: img.data_base64, pending: false }
        : m
      ));
      toast('Image générée', 'success');
    } catch (err) {
      console.warn('YNX: auto image gen failed', err);
      setMessages((cur) => cur.map((m) => m.id === asstId
        ? { ...m, content: `⚠ Erreur génération image : ${err.message}`, error: true, pending: false }
        : m
      ));
      toast(`Image : ${err.message}`, 'error');
    } finally {
      setStreaming(false);
    }
  };

  // ─── AUTO VIDEO GENERATION (intent routed) ───
  const handleAutoVideo = async (prompt, referenceAttachment) => {
    const userMsg = {
      id: uid(),
      role: 'user',
      content: prompt,
      attachments: referenceAttachment ? [{ name: referenceAttachment.name, mime_type: referenceAttachment.mime_type }] : [],
      ts: Date.now(),
    };
    const asstId = uid();
    const placeholder = {
      id: asstId,
      role: 'assistant',
      content: '🎬 Préparation de la vidéo Runway…',
      ts: Date.now(),
      pending: true,
    };
    setMessages((cur) => [...cur, userMsg, placeholder]);
    setInput('');
    resetTextarea();
    setAttachments([]);
    setStreaming(true);

    try {
      // Step 1: get a source image. If user attached one, use it. Otherwise generate one first.
      let sourceB64 = referenceAttachment?.data_base64 || null;
      if (!sourceB64) {
        setMessages((cur) => cur.map((m) => m.id === asstId
          ? { ...m, content: '🎨 Étape 1/2 — création de l\'image source (Nano Banana)…' }
          : m
        ));
        const imgResult = await generateImage({ prompt });
        const img = (imgResult.images || [])[0];
        if (!img?.data_base64) throw new Error('Impossible de créer l\'image source');
        sourceB64 = img.data_base64;
      }

      // Step 2: submit to Runway
      setMessages((cur) => cur.map((m) => m.id === asstId
        ? { ...m, content: '🎬 Étape 2/2 — animation Runway (30-90 secondes)…', sourceImage: sourceB64 }
        : m
      ));
      const { task_id } = await generateVideo({
        promptImageBase64: sourceB64,
        promptText: prompt,
        duration: 5,
      });
      if (!task_id) throw new Error('Pas de task_id Runway');

      const data = await pollVideoUntilDone(task_id, {
        onProgress: ({ status, progress }) => {
          setMessages((cur) => cur.map((m) => m.id === asstId
            ? { ...m, content: `🎬 ${status} · ${Math.round((progress || 0) * 100)}%`, sourceImage: sourceB64 }
            : m
          ));
        },
      });
      const url = (data.output && data.output[0]) || null;
      if (!url) throw new Error('Aucune URL vidéo retournée');
      setMessages((cur) => cur.map((m) => m.id === asstId
        ? { ...m, content: 'Vidéo prête !', generatedVideoUrl: url, sourceImage: sourceB64, pending: false }
        : m
      ));
      toast('Vidéo prête !', 'success');
    } catch (err) {
      console.warn('YNX: auto video gen failed', err);
      setMessages((cur) => cur.map((m) => m.id === asstId
        ? { ...m, content: `⚠ Erreur vidéo : ${err.message}`, error: true, pending: false }
        : m
      ));
      toast(`Vidéo : ${err.message}`, 'error');
    } finally {
      setStreaming(false);
    }
  };

  // Group models by provider for the select. Merge static MODELS + live-discovered models.
  // Dedupe live models that already exist in static MODELS by raw model name.
  const staticRaws = new Set(MODELS.map((m) => (m.id || '').toLowerCase()));
  const liveExtra = (liveModels || []).filter((m) => {
    const rawLower = (m.raw || '').toLowerCase();
    // Skip if the raw model already appears in any static entry (by name match)
    for (const sm of MODELS) {
      if ((sm.name || '').toLowerCase().includes(rawLower.split('/').pop()?.slice(0, 22) || '__nope__')) return false;
    }
    return !staticRaws.has(m.id.toLowerCase());
  });
  const allModels = [...MODELS, ...liveExtra];
  // Dedupe by id to avoid React duplicate-key warnings (providers occasionally
  // return the same model id more than once or static/live can collide).
  const seenIds = new Set();
  const dedupedModels = allModels.filter((m) => {
    if (seenIds.has(m.id)) return false;
    seenIds.add(m.id);
    return true;
  });
  const grouped = dedupedModels.reduce((acc, m) => {
    (acc[m.provider] = acc[m.provider] || []).push(m);
    return acc;
  }, {});

  const tokenCount = messages.reduce((sum, m) => sum + Math.ceil((m.content || '').length / 4), 0);

  // ─── BUSINESS-FIRST ROUTING ────────────────────────────
  // 0. Master admin route — Carl TACITA only (via /?admin=SECRET)
  if (isAdminRoute) {
    return <MasterAdmin initialSecret={adminSecretFromUrl} />;
  }
  // 0bis. Emergent OAuth callback — process session_id before any other UI
  if (authCallbackBusy) {
    return (
      <div className='App' style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <div style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
          Connexion Google en cours…
        </div>
      </div>
    );
  }
  // 1. Loading splash
  if (workspaceLoading) {
    return (
      <div className='App' style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <div style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>Chargement YNX…</div>
      </div>
    );
  }
  // 2. Workspace blocked by Carl
  if (workspaceBlocked) {
    return (
      <div className='ynx-blocked-wrap'>
        <div className='ynx-blocked-card'>
          <div className='ynx-blocked-icon'>
            <Square size={32} />
          </div>
          <h1 className='ynx-blocked-title'>Espace suspendu</h1>
          <p className='ynx-blocked-msg'>
            Cet espace entreprise a été suspendu par l&apos;administrateur YNX.<br />
            Pour toute information ou réactivation, veuillez contacter <b>Carl TACITA</b>.
          </p>
          <a href='mailto:carl@tacita.io' className='biz-btn-primary' style={{ display: 'inline-flex', width: 'auto', textDecoration: 'none' }}>
            Contacter Carl TACITA
          </a>
          <button
            className='biz-btn-ghost sm'
            style={{ marginTop: 16 }}
            onClick={() => { clearLocalWorkspace(); window.location.reload(); }}
          >
            Quitter cet espace sur ce navigateur
          </button>
          <div className='ynx-blocked-foot'>
            YNX by Carl TACITA · OS IA des Entreprises
          </div>
        </div>
      </div>
    );
  }
  // 3. No workspace yet → Landing page first, then onboarding on demand
  if (!workspace) {
    if (showOnboarding) {
      return (
        <BusinessOnboarding
          initialMode={onboardingMode}
          onReady={(ws) => setWorkspace(ws)}
          onBack={() => setShowOnboarding(false)}
          currentUser={currentUser}
          setCurrentUser={setCurrentUser}
        />
      );
    }
    return (
      <LandingPage
        onStart={() => { setOnboardingMode('create'); setShowOnboarding(true); }}
        onJoin={() => { setOnboardingMode('join'); setShowOnboarding(true); }}
      />
    );
  }
  // Workspace ready → show Business Hub by default, Chat optional.
  if (view === 'business') {
    return (
      <BusinessHub
        workspace={workspace}
        setWorkspace={setWorkspace}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        openChat={() => setView('chat')}
        openStudio={() => { setView('chat'); setShowImageGen(true); }}
      />
    );
  }

  return (
    <div className='App' translate='no'>
      {/* HEADER */}
      <header className='voanh-header'>
        <div className='brand' onClick={() => setView('business')}>
          <div className='brand-logo'><VoanhLogo size={36} /></div>
          <div className='brand-text'>
            <div className='brand-name'>YNX</div>
            <div className='brand-sub'>BY CARL TACITA · OS IA ENTREPRISES</div>
          </div>
        </div>

        <div className='header-controls desktop-only'>
          <select data-testid='model-select' name='model-select' className='hud-select' value={model} onChange={(e) => setModel(e.target.value)} title='Modèle IA'>
            {Object.entries(grouped).map(([prov, list]) => (
              <optgroup key={prov} label={prov}>
                {list.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </optgroup>
            ))}
          </select>

          <select data-testid='agent-select' name='agent-select' className='hud-select' value={agent?.id || ''} onChange={async (e) => {
            const id = e.target.value;
            if (!id) { setAgent(null); return; }
            const a = await db.get('agents', id);
            setAgent(a || null);
          }} title='Agent'>
            <option value=''>— Aucun agent —</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>

          <select data-testid='theme-select' name='theme-select' className='hud-select' value={theme} onChange={(e) => setTheme(e.target.value)} title='Thème'>
            {THEMES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          <span className='status-pill active'><span className='status-dot' /> ONLINE</span>
        </div>

        <div className='header-right desktop-only'>
          <button
            className='hud-btn'
            onClick={() => setView('business')}
            title="Retour à l'espace entreprise"
            data-testid='btn-back-business'
            style={{ background: 'var(--cyan-dim)', color: 'var(--cyan)', borderColor: 'var(--cyan)' }}
          >
            <Briefcase size={13} /> ENTREPRISE
          </button>
          <button data-testid='btn-memory' className='hud-btn' onClick={() => setShowMemory(!showMemory)} title='Mémoire'><Brain size={13} /> MEMORY</button>
          <button data-testid='btn-agents' className='hud-btn' onClick={() => setShowAgentModal(true)} title='Agents'><Bot size={13} /> AGENTS</button>
          <button data-testid='btn-data' className='hud-btn' onClick={() => setShowDataModal(true)} title='Données'><Database size={13} /> DATA</button>
          <button data-testid='btn-news' className='hud-btn' onClick={() => setShowNews(true)} title='Actualités IA en direct'><Newspaper size={13} /> NEWS</button>
          <button data-testid='btn-api' className='hud-btn' onClick={() => setShowApiModal(true)} title='API'><Key size={13} /> API</button>
          <button data-testid='btn-clear' className='hud-btn' onClick={clearChat} title='Effacer'><RotateCcw size={13} /></button>
          <button data-testid='btn-new' className='hud-btn primary' onClick={newChat} title='Nouveau'><Plus size={13} /> NEW</button>
        </div>

        <button className='hud-btn burger-btn' onClick={(e) => { e.stopPropagation(); setBurgerOpen(!burgerOpen); }}>
          {burgerOpen ? <X size={16} /> : <Menu size={16} />}
        </button>
      </header>

      {/* MOBILE MENU */}
      <div className={`mobile-menu ${burgerOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
        <span className='status-pill active'><span className='status-dot' /> ONLINE</span>
        <select name='model-select-mobile' className='hud-select' value={model} onChange={(e) => setModel(e.target.value)}>
          {Object.entries(grouped).map(([prov, list]) => (
            <optgroup key={prov} label={prov}>
              {list.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </optgroup>
          ))}
        </select>
        <select name='agent-select-mobile' className='hud-select' value={agent?.id || ''} onChange={async (e) => {
          const id = e.target.value;
          if (!id) { setAgent(null); return; }
          const a = await db.get('agents', id);
          setAgent(a || null);
        }}>
          <option value=''>— Aucun agent —</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select name='theme-select-mobile' className='hud-select' value={theme} onChange={(e) => setTheme(e.target.value)}>
          {THEMES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button className='hud-btn' onClick={() => { setBurgerOpen(false); setShowMemory(true); }}><Brain size={13} /> MEMORY</button>
        <button className='hud-btn' onClick={() => { setBurgerOpen(false); setShowAgentModal(true); }}><Bot size={13} /> AGENTS</button>
        <button className='hud-btn' onClick={() => { setBurgerOpen(false); setShowDataModal(true); }}><Database size={13} /> DATA</button>
        <button className='hud-btn' onClick={() => { setBurgerOpen(false); setShowApiModal(true); }}><Key size={13} /> API</button>
        <button className='hud-btn' onClick={() => { setBurgerOpen(false); clearChat(); }}><RotateCcw size={13} /> EFFACER</button>
        <button className='hud-btn primary' onClick={() => { setBurgerOpen(false); newChat(); }}><Plus size={13} /> NOUVELLE</button>
      </div>

      {/* CHAT */}
      <div className='chat-container' ref={chatRef} onClick={() => setBurgerOpen(false)}>
        <div className='chat-inner'>
          {messages.length === 0 ? (
            <div className='welcome-screen'>
              <div className='welcome-logo'><VoanhLogo size={72} /></div>
              <div className='welcome-title'>YNX</div>
              <div className='welcome-sub'>NEURAL CORE · ONLINE</div>
              <div className='welcome-desc'>
                Votre intelligence artificielle personnelle pour cloner des sites et créer des logiciels.
                Choisissez un modèle, activez un agent et commencez à dialoguer.
              </div>
              <div className='welcome-prompts'>
                {SAMPLE_PROMPTS.map((p) => (
                  <button key={p.text} className='prompt-chip' onClick={() => selectPrompt(p.text)}>
                    ▸ {p.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => {
              const isLast = i === messages.length - 1;
              const blocks = m.role === 'assistant' && m.content && !streaming ? extractCodeBlocks(m.content) : [];
              return (
                <div key={m.id} className={`message ${m.role}`}>
                  <div className='message-role'>
                    {m.role === 'user' ? '> USER' : `< YNX · ${MODELS.find((x) => x.id === model)?.name || model}`}
                    {agent && m.role === 'assistant' && ` · ${agent.name}`}
                    {m.role === 'assistant' && m.routedTo && (
                      <span className='routed-badge' data-testid='routed-badge'>→ {m.routedTo}</span>
                    )}
                  </div>
                  {m.role === 'user' && m.attachments && m.attachments.length > 0 && (
                    <div className='msg-attachments'>
                      {m.attachments.map((att, k) => (
                        <span key={`${att.name || 'att'}-${att.size || 0}-${k}`} className='msg-att-chip'>
                          {(att.mime_type || '').startsWith('image/') ? <ImageIcon size={11} /> : <FileText size={11} />}
                          {att.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className='message-body'>
                    <MarkdownView>{m.content}</MarkdownView>
                    {isLast && streaming && m.role === 'assistant' && <span className='caret' />}
                    {m.generatedImage && (
                      <div className='generated-media'>
                        <img src={`data:image/png;base64,${m.generatedImage}`} alt='généré' />
                        <div className='generated-actions'>
                          <a
                            href={`data:image/png;base64,${m.generatedImage}`}
                            download={`ynx-image-${m.id}.png`}
                            className='hud-btn primary'
                            style={{ textDecoration: 'none' }}
                          >
                            <Download size={12} /> TÉLÉCHARGER
                          </a>
                          <button
                            className='hud-btn'
                            onClick={() => { attachBase64Image(m.generatedImage, `image-${m.id}.png`); }}
                            title='Réutiliser cette image dans le prochain message'
                          >
                            <ImageIcon size={12} /> RÉUTILISER
                          </button>
                          <button
                            className='hud-btn'
                            onClick={() => { setVideoSource(m.generatedImage); setShowVideoGen(true); }}
                            title='Animer cette image en vidéo'
                          >
                            <Video size={12} /> ANIMER
                          </button>
                        </div>
                      </div>
                    )}
                    {m.generatedVideoUrl && (
                      <div className='generated-media'>
                        <video src={m.generatedVideoUrl} controls />
                        <div className='generated-actions'>
                          <a
                            href={m.generatedVideoUrl}
                            target='_blank'
                            rel='noreferrer'
                            className='hud-btn primary'
                            style={{ textDecoration: 'none' }}
                          >
                            <Download size={12} /> OUVRIR / TÉLÉCHARGER
                          </a>
                        </div>
                      </div>
                    )}
                    {m.pending && (
                      <div className='pending-spinner'>
                        <span className='spin-ring' />
                      </div>
                    )}
                  </div>
                  {blocks.length > 0 && (
                    <div className='msg-actions'>
                      {blocks.length > 1 && (
                        <button
                          data-testid='msg-zip-btn'
                          className='hud-btn primary'
                          onClick={() => handleDownloadZip(m)}
                          title='Télécharger tous les fichiers en .zip'
                        >
                          <Package size={12} /> .ZIP ({blocks.length} fichiers)
                        </button>
                      )}
                      {blocks.map((b, idx) => {
                        const canXlsx = b.lang === 'csv' || b.ext === 'csv' || b.lang === 'json' || b.lang === 'xlsx';
                        const label = labelForExt(b.ext);
                        return (
                          <React.Fragment key={`${b.ext || 'blk'}-${(b.code || '').slice(0, 12)}-${idx}`}>
                            <button
                              className='hud-btn'
                              onClick={() => handleDownloadOne(b, idx)}
                              title={`Télécharger ce bloc en .${b.ext}`}
                            >
                              <Download size={12} /> {label}
                            </button>
                            {canXlsx && (
                              <button
                                className='hud-btn'
                                style={{ borderColor: 'rgba(0,255,157,0.4)', color: 'var(--neon)' }}
                                onClick={() => handleDownloadXlsx(b, idx)}
                                title='Convertir en vrai fichier Excel (.xlsx)'
                              >
                                <Sheet size={12} /> EXCEL (.xlsx)
                              </button>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* INPUT */}
      <div className='input-bar'>
        {attachments.length > 0 && (
          <div className='attachments-row' data-testid='attachments-row'>
            {attachments.map((a) => (
              <div key={a.id} className='att-chip'>
                {a.mime_type.startsWith('image/') ? <ImageIcon size={12} /> : <FileText size={12} />}
                <span className='att-name'>{a.name}</span>
                <span className='att-size'>{humanSize(a.size)}</span>
                <button className='att-remove' onClick={() => removeAttachment(a.id)} title='Retirer'>
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className='input-row'>
          <input
            ref={fileInputRef}
            name='file-upload'
            type='file'
            accept={ACCEPT_ATTR}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = ''; }}
          />
          <div className='input-actions-left'>
            <button
              data-testid='btn-attach'
              className='icon-action-btn'
              onClick={() => fileInputRef.current?.click()}
              disabled={streaming}
              title='Joindre un fichier (image, PDF, texte) — max 8 Mo'
              aria-label='Joindre un fichier'
            >
              <Paperclip size={18} />
            </button>
            <button
              data-testid='btn-mic'
              className={`icon-action-btn ${recording ? 'recording' : ''}`}
              onClick={toggleMic}
              disabled={streaming}
              title={recording ? 'Arrêter le micro' : 'Parler (reconnaissance vocale FR)'}
              aria-label='Microphone'
            >
              {recording ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            <button
              data-testid='btn-webcam'
              className={`icon-action-btn ${showWebcam ? 'active' : ''}`}
              onClick={() => setShowWebcam((v) => !v)}
              disabled={streaming}
              title='Activer la caméra (YNX te voit)'
              aria-label='Caméra'
            >
              <Camera size={18} />
            </button>
            <button
              data-testid='btn-imggen'
              className='icon-action-btn'
              onClick={() => { setImageGenRef(null); setShowImageGen(true); }}
              disabled={streaming}
              title='Générer une image IA (Nano Banana)'
              aria-label='Générer une image'
            >
              <Sparkles size={18} />
            </button>
            <button
              data-testid='btn-videogen'
              className='icon-action-btn'
              onClick={() => { setVideoSource(null); setShowVideoGen(true); }}
              disabled={streaming}
              title='Générer une vidéo IA (Runway)'
              aria-label='Générer une vidéo'
            >
              <Video size={18} />
            </button>
            <button
              data-testid='btn-clone-url'
              className='icon-action-btn'
              onClick={() => setShowCloneModal(true)}
              disabled={streaming}
              title="Cloner un site web à partir de son URL"
              aria-label='Cloner une URL'
            >
              <Globe size={18} />
            </button>
            <button
              data-testid='btn-pwa-gen'
              className='icon-action-btn'
              onClick={() => setShowPwaGen(true)}
              disabled={streaming}
              title='Générer une PWA (app mobile installable)'
              aria-label='Générer une PWA'
            >
              <Smartphone size={18} />
            </button>
            <button
              data-testid='btn-promo'
              className='icon-action-btn'
              onClick={() => setShowPromoGen(true)}
              disabled={streaming}
              title='Music Promo Engine — pack de croissance légitime pour ta musique'
              aria-label='Music Promo Engine'
            >
              <Music size={18} />
            </button>
            <button
              data-testid='btn-audio-studio'
              className='icon-action-btn'
              onClick={async () => {
                try {
                  toast?.('Génération du Studio Audio…', 'info');
                  const payload = await generateAudioStudio();
                  await downloadPwaZip(payload);
                  toast?.('Studio Audio téléchargé 🎚️ — glisse le .zip sur Netlify Drop', 'success');
                } catch (e) {
                  toast?.(`Erreur Audio Studio: ${e.message}`, 'error');
                }
              }}
              disabled={streaming}
              title='Audio Studio — EQ / Compression / Limiter / Export WAV-MP3 (PWA installable)'
              aria-label='Audio Studio'
            >
              <Headphones size={18} />
            </button>
            <button
              data-testid='btn-tts'
              className={`icon-action-btn ${ttsEnabled ? 'active' : ''}`}
              onClick={toggleTts}
              title={ttsEnabled ? 'YNX te parle — clic pour couper' : 'Activer la lecture vocale (YNX te parle)'}
              aria-label='Lecture vocale'
            >
              {ttsEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
          </div>
          <div className='input-area'>
            <textarea
              data-testid='chat-input'
              name='chat-input'
              ref={inputRef}
              className='input-prompt'
              placeholder={streaming ? 'YNX génère la réponse…' : 'Tapez votre requête… (Entrée = envoyer · Shift+Entrée = saut de ligne)'}
              value={input}
              onChange={handleInput}
              onKeyDown={onKeyDown}
              disabled={streaming}
              rows={1}
            />
            <div className='input-meta'>
              <span>{agent ? `▸ AGENT : ${agent.name}` : '▸ MODE NEUTRE'} · {memories.length} mém. · {attachments.length} fichier(s)</span>
              <span>{messages.length} msg · ~{tokenCount} tokens</span>
            </div>
          </div>
          {streaming ? (
            <button className='send-btn' onClick={stopStream} title='Arrêter' style={{ background: 'linear-gradient(135deg, var(--danger), #ff8) !important' }}>
              <Square size={14} fill='currentColor' /> STOP
            </button>
          ) : (
            <button data-testid='send-btn' className='send-btn' onClick={send} disabled={!input.trim() && attachments.length === 0}>
              <Send size={14} /> SEND
            </button>
          )}
        </div>
      </div>

      {/* PANELS / MODALS */}
      <MemoryPanel open={showMemory} onClose={() => setShowMemory(false)} onChange={refreshMemories} />
      <ApiModal open={showApiModal} onClose={() => setShowApiModal(false)} />
      <AgentModal
        open={showAgentModal}
        onClose={() => setShowAgentModal(false)}
        currentAgent={agent}
        onAgentChange={async (a) => { setAgent(a); await refreshAgents(); }}
      />
      <DataModal
        open={showDataModal}
        onClose={() => setShowDataModal(false)}
        onAfterImport={async () => {
          await refreshAgents();
          await refreshMemories();
          // reload current chat
          if (chatId) {
            const c = await db.get('chats', chatId);
            if (c?.messages) setMessages(c.messages);
          }
        }}
      />

      {/* CLONE URL MODAL */}
      {showCloneModal && (
        <div className='modal-overlay active' onClick={(e) => e.target === e.currentTarget && setShowCloneModal(false)}>
          <div className='modal'>
            <div className='modal-header'>
              <span className='modal-title'>CLONER UN SITE WEB</span>
              <button className='modal-close' onClick={() => setShowCloneModal(false)}><X size={14} /></button>
            </div>
            <div className='modal-body'>
              <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 16, lineHeight: 1.6 }}>
                Collez l&apos;URL d&apos;un site existant. YNX va récupérer son HTML, sa structure et ses images, puis demander à l&apos;IA de générer un clone fidèle que vous pourrez télécharger en .zip.
              </p>
              <label className='modal-label'>▸ URL à cloner</label>
              <input
                data-testid='clone-url-input'
                name='clone-url-input'
                className='hud-input'
                placeholder='https://exemple.com'
                value={cloneUrlInput}
                onChange={(e) => setCloneUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCloneUrl(); } }}
                autoFocus
              />
              <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 10, lineHeight: 1.5 }}>
                ▸ Astuce : choisissez d&apos;abord un modèle puissant (GPT-5 / Claude Sonnet 4.5) pour de meilleurs résultats.<br />
                ▸ Le HTML extrait est tronqué à 30 Ko pour rester dans la fenêtre de contexte.
              </p>
            </div>
            <div className='modal-footer'>
              <button className='hud-btn' onClick={() => setShowCloneModal(false)} disabled={cloning}>ANNULER</button>
              <button data-testid='clone-url-go' className='hud-btn primary' onClick={handleCloneUrl} disabled={cloning || !cloneUrlInput.trim()}>
                {cloning ? <><span className='spin-ring' /> RÉCUPÉRATION…</> : <><Globe size={12} /> RÉCUPÉRER & PRÉPARER LE PROMPT</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WEBCAM PANEL */}
      <WebcamPanel
        open={showWebcam}
        onClose={() => setShowWebcam(false)}
        currentModel={model}
        onAttachSnapshot={(b64) => attachBase64Image(b64, `webcam-${Date.now()}.png`)}
        onAnalysisResult={(text, snapshotB64) => {
          // Inject the result as an assistant message (offline, no LLM call)
          const userMsg = { id: uid(), role: 'user', content: '📷 Analyse-moi maintenant', attachments: [{ name: 'webcam.png', mime_type: 'image/png' }], ts: Date.now() };
          const asstMsg = { id: uid(), role: 'assistant', content: text, ts: Date.now() };
          setMessages((cur) => [...cur, userMsg, asstMsg]);
          // Also attach the snapshot for further questions if user wants
          attachBase64Image(snapshotB64, `analyse-${Date.now()}.png`);
        }}
        onSendForVideo={(b64) => {
          setVideoSource(b64);
          setShowVideoGen(true);
        }}
      />

      {/* IMAGE GEN MODAL */}
      <ImageGenModal
        open={showImageGen}
        onClose={() => { setShowImageGen(false); setImageGenRef(null); }}
        referenceImageBase64={imageGenRef}
        onAttach={(b64) => attachBase64Image(b64, `ynx-image-${Date.now()}.png`)}
      />

      {/* VIDEO GEN MODAL */}
      <VideoGenModal
        open={showVideoGen}
        onClose={() => { setShowVideoGen(false); setVideoSource(null); }}
        sourceImageBase64={videoSource}
      />

      {/* PWA GEN MODAL */}
      <PwaGenModal
        open={showPwaGen}
        onClose={() => setShowPwaGen(false)}
      />

      {/* PROMO GEN MODAL */}
      <PromoGenModal
        open={showPromoGen}
        onClose={() => setShowPromoGen(false)}
      />

      {/* AI NEWS PANEL */}
      <AiNewsPanel
        open={showNews}
        onClose={() => setShowNews(false)}
        onUseAsPrompt={(item) => {
          const prompt = `Analyse cette actu IA et donne-moi : 1) un résumé clair, 2) ce que ça change pour les utilisateurs/devs, 3) les modèles ou outils concrets à essayer.\n\nTitre : ${item.title}\nSource : ${item.domain}\nURL : ${item.url}\nExtrait : ${item.snippet || '—'}`;
          setInput(prompt);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
      />
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <VoanhApp />
        <PWAInstallPrompt />
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
